import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit3, Trash2, CheckCircle, Sparkles, FileText, Loader, X, CalendarClock, Send, ExternalLink, Eye, Link2, ImagePlus, Images } from 'lucide-react';
import api from '../lib/api';
import { ensureInsidePostLink, replaceSavedPostUrl } from '../lib/postLinks';
import MediaEditorModal from '../components/post-media/MediaEditorModal';
import OrganizationLogoAvatar from '../components/OrganizationLogoAvatar';
import { asArray } from '../lib/safeData';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUS_STYLES = {
  draft: 'bg-secondary/50 text-muted-foreground border-border',
  approved: 'bg-primary/10 text-primary border-primary/20',
  scheduled: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  publishing: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  published: 'bg-status-up/10 text-status-up border-status-up/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20',
};

function firstUrl(...values) {
  for (const value of values) {
    const match = String(value || '').match(/https?:\/\/[^\s<>"']+/i);
    if (match) return match[0].replace(/[),.;!?]+$/, '');
  }
  return '';
}

function LinkifiedText({ text }) {
  return String(text || '').split(/(https?:\/\/[^\s<>"']+)/gi).map((part, index) => (
    /^https?:\/\//i.test(part)
      ? <a key={`${part}-${index}`} href={part.replace(/[),.;!?]+$/, '')} target="_blank" rel="noopener noreferrer" className="text-[#70b5f9] hover:underline" onClick={event => event.stopPropagation()}>{part}</a>
      : part
  ));
}

async function uploadPictures(postId, files) {
  for (const file of files) {
    await api.post(`/post-images/for-post/${postId}`, file, {
      headers: {
        'Content-Type': file.type,
        'X-Filename': encodeURIComponent(file.name),
      },
    });
  }
}

function LocalPicture({ file }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return <img src={url} alt={file.name} className="w-full h-20 object-cover rounded-md" />;
}

function PendingPicturePicker({ files, onChange, disabled = false }) {
  const addFiles = (event) => {
    const chosen = Array.from(event.target.files || []);
    onChange([...files, ...chosen].slice(0, 20));
    event.target.value = '';
  };
  return (
    <div className="rounded-lg border border-dashed border-white/15 bg-secondary/10 p-3 space-y-3" data-testid="pending-picture-picker">
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-sm font-medium text-foreground">Pictures</p><p className="text-[11px] text-muted-foreground">JPG, PNG, or WebP · up to 20 pictures · 20 MB each</p></div>
        <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 bg-primary/10 text-primary text-xs font-medium ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-primary/20'}`}>
          <ImagePlus className="w-3.5 h-3.5" /> Add pictures
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={disabled} onChange={addFiles} className="hidden" data-testid="post-picture-file-input" />
        </label>
      </div>
      {files.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{files.map((file, index) => <div key={`${file.name}-${file.lastModified}-${index}`} className="relative group"><LocalPicture file={file} /><button type="button" onClick={() => onChange(files.filter((_, itemIndex) => itemIndex !== index))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center" aria-label={`Remove ${file.name}`}><X className="w-3 h-3" /></button><p className="text-[10px] text-muted-foreground truncate mt-1">{file.name}</p></div>)}</div>}
    </div>
  );
}

function SavedPictureGrid({ images, removable = false, onRemove, onAltText, onAdjust }) {
  if (!images?.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="saved-picture-grid">
      {images.map(image => (
        <div key={image.id} className="rounded-lg border border-white/10 bg-secondary/20 p-2 space-y-2">
          <div className="relative"><img src={image.content_url} alt={image.alt_text || image.filename} className="w-full h-24 object-cover rounded-md" />{removable && <button type="button" onClick={() => onRemove(image)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center" aria-label={`Remove ${image.filename}`}><X className="w-3 h-3" /></button>}</div>
          {onAltText && <input defaultValue={image.alt_text || ''} onBlur={event => onAltText(image, event.target.value)} placeholder="Alt text" className="w-full bg-background/30 border border-white/10 rounded px-2 py-1 text-[11px] text-foreground outline-none" />}
          {onAdjust && image?.id && (
            <button
              type="button"
              onClick={() => onAdjust(image)}
              className="w-full min-h-9 px-2 py-1.5 rounded-md border border-primary/30 bg-primary/10 text-primary text-[11px] font-medium touch-manipulation"
              data-testid={`adjust-picture-${image.id}`}
              aria-label={`Adjust picture ${image.filename || ''}`.trim()}
            >
              Adjust picture
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function PostPicturesEditor({ postId, initialImages = [] }) {
  const [images, setImages] = useState(initialImages);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);

  useEffect(() => {
    api.get(`/post-images/for-post/${postId}`).then(response => setImages(asArray(response.data))).catch(() => {});
  }, [postId]);

  const addPictures = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      await uploadPictures(postId, files);
      const response = await api.get(`/post-images/for-post/${postId}`);
      setImages(asArray(response.data));
      toast.success(`${files.length} picture${files.length === 1 ? '' : 's'} added`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Picture upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removePicture = async (image) => {
    try {
      await api.delete(`/post-images/${image.id}`);
      setImages(current => current.filter(item => item.id !== image.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove picture');
    }
  };

  const saveAltText = async (image, altText) => {
    try {
      const response = await api.patch(`/post-images/${image.id}`, { alt_text: altText || null });
      setImages(current => current.map(item => item.id === image.id ? response.data : item));
    } catch { toast.error('Could not save picture description'); }
  };

  const handleImageSaved = (updated) => {
    setImages(current => current.map(item => item.id === updated.id ? { ...item, ...updated } : item));
    setEditingImage(current => (current?.id === updated.id ? { ...current, ...updated } : current));
  };

  return (
    <div className="rounded-lg border border-white/10 bg-secondary/20 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-2"><Images className="w-4 h-4 text-primary" /><div><h4 className="text-sm font-medium text-foreground">Pictures</h4><p className="text-[11px] text-muted-foreground">These publish with the post, including scheduled posts.</p></div></div><label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 bg-primary/10 text-primary text-xs font-medium ${uploading || images.length >= 20 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-primary/20'}`}>{uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}{uploading ? 'Uploading…' : 'Add pictures'}<input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading || images.length >= 20} onChange={addPictures} className="hidden" data-testid="edit-post-picture-file-input" /></label></div>
      <SavedPictureGrid images={images} removable onRemove={removePicture} onAltText={saveAltText} onAdjust={setEditingImage} />
      {!images.length && <p className="text-xs text-muted-foreground">No pictures attached yet.</p>}
      {editingImage && (
        <MediaEditorModal
          image={editingImage}
          onClose={() => setEditingImage(null)}
          onSaved={handleImageSaved}
        />
      )}
    </div>
  );
}

function LinkedInPictureGrid({ images }) {
  if (!images?.length) return null;
  const shown = images.slice(0, 4);
  return <div className={`grid gap-0.5 ${shown.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>{shown.map((image, index) => <div key={image.id} className="relative"><img src={image.content_url} alt={image.alt_text || image.filename} className={`w-full object-cover ${shown.length === 1 ? 'max-h-[360px]' : 'h-44'}`} />{index === 3 && images.length > 4 && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-2xl font-semibold">+{images.length - 4}</div>}</div>)}</div>;
}

function LinkedInPreview({ post }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#1B2128', border: '1px solid #2d3748' }}>
      <div className="flex items-start gap-3 p-4">
        <OrganizationLogoAvatar sizeClass="w-12 h-12" textClass="text-lg" />
        <div>
          <div className="font-semibold text-sm" style={{ color: '#E2E8F0' }}>Mayr Technologies <span style={{ color: '#8892a4', fontWeight: 400 }}>· 1st</span></div>
          <div className="text-xs" style={{ color: '#8892a4' }}>Marketing &amp; Technology</div>
          <div className="text-xs" style={{ color: '#8892a4' }}>Just now · 🌐</div>
        </div>
      </div>
      <div className="px-4 pb-4 text-sm leading-relaxed whitespace-pre-wrap break-words" style={{ color: '#E2E8F0' }}><LinkifiedText text={post.content} /></div>
      <LinkedInPictureGrid images={post.images} />
      <div className="px-4 py-2.5 flex items-center gap-5 text-xs" style={{ borderTop: '1px solid #2d3748', color: '#8892a4' }}>
        {['👍 Like', '💬 Comment', '🔁 Repost', '📤 Send'].map(label => <span key={label}>{label}</span>)}
      </div>
      {post.first_comment && (
        <div className="p-4 flex gap-2" style={{ borderTop: '1px solid #2d3748' }}>
          <OrganizationLogoAvatar sizeClass="w-8 h-8" textClass="text-xs" />
          <div className="rounded-xl px-3 py-2 text-xs whitespace-pre-wrap break-words" style={{ background: '#242b33', color: '#E2E8F0' }}>
            <div className="font-semibold mb-0.5">Mayr Technologies</div>
            <LinkifiedText text={post.first_comment} />
          </div>
        </div>
      )}
    </div>
  );
}

function RedditPreview({ post }) {
  return (
    <div className="rounded overflow-hidden" style={{ background: '#1A1A1B', border: '1px solid #343536' }}>
      <div className="flex">
        <div className="w-10 flex flex-col items-center py-3 gap-1 flex-shrink-0" style={{ background: '#161617', color: '#818384' }}><span>▲</span><span className="text-xs font-bold">1</span><span>▼</span></div>
        <div className="flex-1 p-3">
          <div className="text-xs mb-1.5" style={{ color: '#818384' }}><span className="font-bold" style={{ color: '#D7DADC' }}>r/announcements</span>{' · Posted by u/mayr_ops'}</div>
          <div className="text-base font-medium mb-2" style={{ color: '#D7DADC' }}>{post.title}</div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap break-words mb-3" style={{ color: '#9CA3AF' }}><LinkifiedText text={post.content} /></div>
          <div className="flex gap-4 text-xs" style={{ color: '#818384' }}>{['💬 0 Comments', '📤 Share', '⭐ Award', '💾 Save'].map(label => <span key={label}>{label}</span>)}</div>
        </div>
      </div>
    </div>
  );
}

function PostPreviewModal({ post, onClose }) {
  const [tab, setTab] = useState(post.platform === 'reddit' ? 'reddit' : 'linkedin');
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="glass rounded-xl border border-foreground/10 w-full max-w-lg shadow-2xl max-h-[calc(100vh-2rem)] flex flex-col" onClick={event => event.stopPropagation()} data-testid="post-preview-modal">
        <div className="flex items-center justify-between p-4 border-b border-foreground/5 flex-shrink-0">
          <div className="flex items-center gap-2"><Eye className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold text-foreground">Post Preview</h3></div>
          <div className="flex items-center gap-2">
            {['linkedin', 'reddit'].map(platform => <button key={platform} type="button" onClick={() => setTab(platform)} className={`px-3 py-1 rounded-lg text-xs font-medium capitalize ${tab === platform ? 'bg-primary/20 border border-primary/30 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`preview-tab-${platform}`}>{platform}</button>)}
            <button type="button" onClick={onClose} className="ml-1"><X className="w-4 h-4 text-muted-foreground" /></button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto overscroll-contain min-h-0">
          {tab === 'linkedin' ? <LinkedInPreview post={post} /> : <RedditPreview post={post} />}
          <p className="text-[10px] text-muted-foreground/50 text-center mt-3">Preview only — actual rendering may vary by platform</p>
        </div>
      </div>
    </div>
  );
}

function AIGenerateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ topic: '', tone: 'professional', campaign: '', platform: 'linkedin', additional_context: '' });
  const [loading, setLoading] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/posts/generate-ai', { ...form, campaign: form.campaign || null, additional_context: form.additional_context || null });
      toast.success('AI post generated!');
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="ai-generate-modal">
      <div className="glass rounded-xl border border-white/10 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-foreground">AI Post Generator</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-lg leading-none">&times;</button>
        </div>
        <form onSubmit={handleGenerate} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Topic / Key Message *</label>
            <textarea required value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} rows={3} placeholder="e.g. Launching our new email verification feature that reduces bounce rates by 40%" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors resize-none" data-testid="ai-topic-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tone</label>
              <select value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground outline-none" data-testid="ai-tone-select">
                {['professional', 'friendly', 'authoritative', 'conversational', 'inspiring'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Platform</label>
              <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground outline-none" data-testid="ai-platform-select">
                {['linkedin', 'twitter', 'reddit'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Campaign (optional)</label>
            <input value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} placeholder="Q1 Product Launch" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors" data-testid="ai-campaign-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Additional Context (optional)</label>
            <textarea value={form.additional_context} onChange={e => setForm({ ...form, additional_context: e.target.value })} rows={2} placeholder="Target audience, specific points to include..." className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors resize-none" data-testid="ai-context-input" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-muted-foreground text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="ai-generate-submit-btn">
              {loading ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5" /> Generate</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPostModal({ post, onClose, onSaved }) {
  const publishedCopy = post.status === 'published';
  const detectedUrl = post.cta_url || firstUrl(post.content, post.first_comment);
  const originalUrl = useRef(detectedUrl);
  const [form, setForm] = useState({
    title: post.title,
    content: post.content,
    campaign: post.campaign || '',
    cta_type: post.cta_type || (post.first_comment ? 'comment' : 'in_post'),
    cta_url: detectedUrl,
    first_comment: post.first_comment || '',
  });
  const [loading, setLoading] = useState(false);
  const [pendingPictures, setPendingPictures] = useState([]);

  const handleLinkChange = (event) => {
    const nextUrl = event.target.value;
    setForm(current => ({ ...current, cta_url: nextUrl }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let content = replaceSavedPostUrl(form.content, originalUrl.current, form.cta_url);
      if (form.cta_type === 'in_post') {
        content = ensureInsidePostLink(content, form.cta_url);
      }
      const payload = {
        ...form,
        content,
        campaign: form.campaign || null,
        cta_url: form.cta_url || null,
        first_comment: replaceSavedPostUrl(form.first_comment, originalUrl.current, form.cta_url) || null,
      };
      const res = publishedCopy
        ? await api.post(`/posts/${post.id}/copy`, payload)
        : await api.put(`/posts/${post.id}`, payload);
      let saved = res.data;
      let pictureUploadFailed = false;
      if (publishedCopy && pendingPictures.length) {
        try {
          await uploadPictures(saved.id, pendingPictures);
          saved = (await api.get(`/posts/${saved.id}`)).data;
        } catch (error) {
          pictureUploadFailed = true;
          toast.error(error.response?.data?.detail || 'Draft created, but a picture could not be added');
          saved = (await api.get(`/posts/${saved.id}`)).data;
        }
      }
      if (!pictureUploadFailed) toast.success(publishedCopy ? 'Editable draft copy created' : 'Post updated');
      onSaved(saved, publishedCopy);
      onClose();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update post'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-post-modal">
      <div className="glass rounded-xl border border-white/10 w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div><h3 className="font-heading font-bold text-foreground">{publishedCopy ? 'Edit Copy for Reposting' : 'Edit Post'}</h3>{publishedCopy && <p className="text-xs text-muted-foreground mt-0.5">Your original published post remains unchanged.</p>}</div>
          <button onClick={onClose}><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Title</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="edit-post-title" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Content</label>
            <textarea required value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={8} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors resize-none" data-testid="edit-post-content" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Campaign</label>
            <input value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="edit-post-campaign" />
          </div>
          <div className="rounded-lg border border-white/10 bg-secondary/20 p-4 space-y-3">
            <div className="flex items-center gap-2"><Link2 className="w-4 h-4 text-primary" /><h4 className="text-sm font-medium text-foreground">Post link</h4></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Link placement</label>
                <select value={form.cta_type} onChange={event => setForm({ ...form, cta_type: event.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground outline-none" data-testid="edit-post-cta-type">
                  <option value="in_post">Inside post</option>
                  <option value="comment">First comment</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Link URL</label>
                <input type="url" value={form.cta_url} onChange={handleLinkChange} placeholder="https://..." className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors" data-testid="edit-post-cta-url" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Inside post automatically adds the Link URL to the end of the post. Changing the URL safely replaces the previous complete URL.</p>
            {form.cta_type === 'comment' && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">First comment text</label>
                <textarea value={form.first_comment} onChange={event => setForm({ ...form, first_comment: event.target.value })} rows={3} placeholder="Add the comment text and link..." className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors resize-none" data-testid="edit-post-first-comment" />
              </div>
            )}
          </div>
          {publishedCopy ? (
            <div className="space-y-3">
              {post.images?.length > 0 && <div><p className="text-xs font-medium text-muted-foreground mb-2">Pictures copied from the published post</p><SavedPictureGrid images={post.images} /></div>}
              <PendingPicturePicker files={pendingPictures} onChange={setPendingPictures} disabled={loading} />
            </div>
          ) : <PostPicturesEditor postId={post.id} initialImages={post.images || []} />}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-muted-foreground text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 transition-colors" data-testid="edit-post-save-btn">
              {loading ? 'Saving...' : publishedCopy ? 'Save as New Draft' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SchedulePostModal({ post, identities, onClose, onScheduled }) {
  const isReschedule = post.status === 'scheduled';
  const defaultIdentity = identities.find(identity => identity.is_default) || identities[0];
  const initial = post.scheduled_at
    ? new Date(`${String(post.scheduled_at).endsWith('Z') ? post.scheduled_at : `${post.scheduled_at}Z`}`)
    : new Date(Date.now() + 60 * 60 * 1000);
  initial.setSeconds(0, 0);
  const localInitial = new Date(initial.getTime() - initial.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [scheduledAt, setScheduledAt] = useState(localInitial);
  const [identityId, setIdentityId] = useState(post.scheduled_identity_id || defaultIdentity?.id || '');
  const [loading, setLoading] = useState(false);

  const handleSchedule = async (event) => {
    event.preventDefault();
    if (!identityId) {
      toast.error('Connect LinkedIn and choose a publishing identity first');
      return;
    }
    setLoading(true);
    try {
      if (post.status === 'draft') {
        await api.post(`/posts/${post.id}/approve`);
      }
      const response = await api.post(`/posts/${post.id}/schedule`, {
        scheduled_at: new Date(scheduledAt).toISOString(),
        identity_id: identityId,
      });
      toast.success(isReschedule ? 'Post rescheduled' : 'Post scheduled');
      onScheduled(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="schedule-post-modal">
      <div className="glass rounded-xl border border-white/10 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold">{isReschedule ? 'Reschedule post' : 'Schedule post'}</h3>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <form onSubmit={handleSchedule} className="p-5 space-y-4">
          <p className="text-[11px] text-muted-foreground" data-testid="schedule-post-disclaimer">
            Schedules this reviewed post for LinkedIn publishing. Separate from Automations “Schedule generation,” which only creates AI drafts.
          </p>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Publish date and time</label>
            <input type="datetime-local" required value={scheduledAt} onChange={event => setScheduledAt(event.target.value)} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="schedule-post-datetime" />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">LinkedIn identity</label>
            <select required value={identityId} onChange={event => setIdentityId(event.target.value)} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="schedule-post-identity">
              <option value="">Select identity</option>
              {identities.map(identity => <option key={identity.id} value={identity.id}>{identity.name} ({identity.type})</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50" data-testid="schedule-post-submit">
              {loading ? 'Saving...' : (isReschedule ? 'Reschedule post' : 'Schedule post')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Posts() {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAIModal, setShowAIModal] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [previewPost, setPreviewPost] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(searchParams.get('add') === 'true');
  const [newPost, setNewPost] = useState({ title: '', content: '', campaign: '', platform: 'linkedin' });
  const [newPostPictures, setNewPostPictures] = useState([]);
  const [creating, setCreating] = useState(false);
  const [identities, setIdentities] = useState([]);
  const [publishLogs, setPublishLogs] = useState([]);
  const [publishingId, setPublishingId] = useState(null);
  const [schedulePost, setSchedulePost] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await api.get('/posts/');
      setPosts(asArray(res.data));
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, []);

  const fetchPublishLogs = useCallback(async () => {
    try {
      const response = await api.get('/linkedin/logs?limit=100');
      setPublishLogs(asArray(response.data));
    } catch {
      setPublishLogs([]);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchPublishLogs();
    api.get('/linkedin/identities').then(response => setIdentities(asArray(response.data))).catch(() => setIdentities([]));
  }, [fetchPosts, fetchPublishLogs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/posts/', { ...newPost, campaign: newPost.campaign || null });
      let saved = res.data;
      if (newPostPictures.length) {
        try {
          await uploadPictures(saved.id, newPostPictures);
          saved = (await api.get(`/posts/${saved.id}`)).data;
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Draft saved, but a picture could not be added');
          saved = (await api.get(`/posts/${saved.id}`)).data;
        }
      }
      toast.success('Post created');
      setPosts([saved, ...posts]);
      setNewPost({ title: '', content: '', campaign: '', platform: 'linkedin' });
      setNewPostPictures([]);
      setShowCreateForm(false);
    } catch { toast.error('Failed to create post'); }
    finally { setCreating(false); }
  };

  const handleApprove = async (post) => {
    try {
      const res = await api.post(`/posts/${post.id}/approve`);
      toast.success('Post approved');
      setPosts(posts.map(p => p.id === post.id ? res.data : p));
    } catch { toast.error('Failed to approve post'); }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success('Post deleted');
      setPosts(posts.filter(p => p.id !== post.id));
    } catch { toast.error('Failed to delete post'); }
  };

  const handlePublish = async (post) => {
    const identity = identities.find(item => item.is_default) || identities[0];
    if (!identity) {
      toast.error('Connect LinkedIn in Settings before publishing');
      return;
    }
    const publishedWindow = window.open('about:blank', '_blank');
    if (publishedWindow) publishedWindow.opener = null;
    setPublishingId(post.id);
    try {
      if (post.status === 'draft' || post.status === 'failed') {
        await api.post(`/posts/${post.id}/approve`);
      }
      const response = await api.post(`/linkedin/publish/${post.id}`, { identity_id: identity.id });
      const publishedUrl = response.data?.linkedin_post_url;
      if (publishedUrl && publishedWindow) {
        publishedWindow.location.replace(publishedUrl);
      } else if (publishedWindow) {
        publishedWindow.close();
      }
      toast.success(publishedUrl ? 'Published — opening the LinkedIn post' : 'Published to LinkedIn');
      await Promise.all([fetchPosts(), fetchPublishLogs()]);
    } catch (error) {
      if (publishedWindow) publishedWindow.close();
      toast.error(error.response?.data?.detail || 'Publishing failed');
      await fetchPosts();
    } finally {
      setPublishingId(null);
    }
  };

  const handleRepost = async (post) => {
    const identity = identities.find(item => item.is_default) || identities[0];
    if (!identity) {
      toast.error('Connect the correct LinkedIn account in Settings before reposting');
      return;
    }
    const confirmed = window.confirm(
      `Repost "${post.title}" to LinkedIn as ${identity.name} (${identity.type})?\n\n` +
      'This creates a new LinkedIn post. The previously published post is not removed.'
    );
    if (!confirmed) return;

    const publishedWindow = window.open('about:blank', '_blank');
    if (publishedWindow) publishedWindow.opener = null;
    setPublishingId(post.id);
    try {
      const response = await api.post(`/linkedin/repost/${post.id}`, { identity_id: identity.id });
      const publishedUrl = response.data?.linkedin_post_url;
      if (publishedUrl && publishedWindow) {
        publishedWindow.location.replace(publishedUrl);
      } else if (publishedWindow) {
        publishedWindow.close();
      }
      toast.success(publishedUrl ? `Reposted as ${identity.name} — opening LinkedIn` : `Reposted as ${identity.name}`);
      await Promise.all([fetchPosts(), fetchPublishLogs()]);
    } catch (error) {
      if (publishedWindow) publishedWindow.close();
      toast.error(error.response?.data?.detail || 'Reposting failed');
      await fetchPosts();
    } finally {
      setPublishingId(null);
    }
  };

  const publishedLinkedInUrl = (post) => {
    const log = publishLogs.find(item =>
      item.post_id === post.id &&
      item.status === 'success' &&
      item.linkedin_post_id?.startsWith('urn:li:')
    );
    return log ? `https://www.linkedin.com/feed/update/${log.linkedin_post_id}/` : null;
  };

  const handleCancelSchedule = async (post) => {
    try {
      const response = await api.post(`/posts/${post.id}/cancel-schedule`);
      toast.success('Schedule cancelled');
      setPosts(posts.map(item => item.id === post.id ? response.data : item));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  const statuses = ['draft', 'approved', 'published'];

  return (
    <div className="space-y-6 fade-in" data-testid="posts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Draft Posts</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAIModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors" data-testid="ai-generate-btn">
            <Sparkles className="w-4 h-4" />
            AI Generate
          </button>
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors active:scale-95" data-testid="create-post-btn">
            <Plus className="w-4 h-4" />
            New Post
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="glass rounded-xl border border-white/10 p-5" data-testid="create-post-form">
          <form onSubmit={handleCreate} className="space-y-4">
            <h3 className="font-heading font-bold text-foreground">New Draft Post</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Title *</label>
                <input required value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })} placeholder="Post title" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors" data-testid="new-post-title" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Platform</label>
                  <select value={newPost.platform} onChange={e => setNewPost({ ...newPost, platform: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground outline-none" data-testid="new-post-platform">
                    {['linkedin', 'twitter', 'reddit'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Campaign</label>
                  <input value={newPost.campaign} onChange={e => setNewPost({ ...newPost, campaign: e.target.value })} placeholder="optional" className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors" data-testid="new-post-campaign" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Content *</label>
              <textarea required value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })} rows={6} placeholder="Write your post content..." className="w-full bg-secondary/30 border border-white/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none transition-colors resize-none" data-testid="new-post-content" />
            </div>
            <PendingPicturePicker files={newPostPictures} onChange={setNewPostPictures} disabled={creating} />
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 rounded-lg border border-white/10 text-muted-foreground text-sm">Cancel</button>
              <button type="submit" disabled={creating} className="px-6 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50 transition-colors" data-testid="submit-create-post-btn">
                {creating ? 'Creating...' : 'Save Draft'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Post list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="glass rounded-xl border border-white/5 p-16 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-foreground mb-2">No Posts Yet</h3>
          <p className="text-muted-foreground text-sm mb-6">Create drafts manually or generate them with AI.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowAIModal(true)} className="px-4 py-2 rounded-lg border border-primary/30 bg-primary/10 text-primary text-sm font-medium transition-colors" data-testid="empty-ai-btn">
              AI Generate
            </button>
            <button onClick={() => setShowCreateForm(true)} className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors" data-testid="empty-create-btn">
              Write Manually
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id} className="glass rounded-xl border border-white/5 hover:border-white/10 transition-colors p-5" data-testid={`post-card-${post.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-heading font-bold text-foreground">{post.title}</h3>
                    {post.ai_generated && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-mono flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> AI
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-mono uppercase ${STATUS_STYLES[post.status] || STATUS_STYLES.draft}`}>{post.status}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{post.platform}</span>
                    {post.images?.length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono flex items-center gap-1"><Images className="w-3 h-3" /> {post.images.length}</span>}
                    {(post.campaign_playbook_name || post.campaign) && (
                      <span className="text-xs text-muted-foreground font-mono" data-testid={`post-campaign-${post.id}`}>
                        · Campaign: {post.campaign_playbook_name || post.campaign}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground font-mono">· {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {post.status === 'draft' && post.platform !== 'linkedin' && (
                    <button onClick={() => handleApprove(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-status-up/10 hover:bg-status-up/20 border border-status-up/20 text-status-up text-xs font-medium transition-colors" data-testid={`approve-post-btn-${post.id}`}>
                      <CheckCircle className="w-3 h-3" />
                      Approve
                    </button>
                  )}
                  {post.platform === 'linkedin' && ['draft', 'approved', 'failed'].includes(post.status) && (
                    <>
                      <button onClick={() => handlePublish(post)} disabled={publishingId === post.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-status-up/10 hover:bg-status-up/20 border border-status-up/20 text-status-up text-xs font-medium disabled:opacity-50" data-testid={`publish-post-btn-${post.id}`}>
                        {publishingId === post.id ? <Loader className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {post.status === 'failed' ? 'Retry Publish' : 'Publish Now'}
                      </button>
                      <button onClick={() => setSchedulePost(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-xs font-medium" data-testid={`schedule-post-btn-${post.id}`}>
                        <CalendarClock className="w-3 h-3" /> Schedule post
                      </button>
                    </>
                  )}
                  {post.platform === 'linkedin' && post.status === 'published' && publishedLinkedInUrl(post) && (
                    <a href={publishedLinkedInUrl(post)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-xs font-medium" data-testid={`view-linkedin-post-btn-${post.id}`}>
                      <ExternalLink className="w-3 h-3" /> View on LinkedIn
                    </a>
                  )}
                  {post.platform === 'linkedin' && post.status === 'published' && (
                    <button onClick={() => handleRepost(post)} disabled={publishingId === post.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-status-up/10 hover:bg-status-up/20 border border-status-up/20 text-status-up text-xs font-medium disabled:opacity-50" data-testid={`repost-linkedin-post-btn-${post.id}`}>
                      {publishingId === post.id ? <Loader className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Repost
                    </button>
                  )}
                  {post.status === 'scheduled' && (
                    <>
                      <button onClick={() => setSchedulePost(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-xs font-medium" data-testid={`reschedule-post-btn-${post.id}`}>
                        <CalendarClock className="w-3 h-3" /> Reschedule post
                      </button>
                      <button onClick={() => handleCancelSchedule(post)} className="px-3 py-1.5 rounded-lg border border-white/10 text-muted-foreground text-xs" data-testid={`cancel-schedule-post-btn-${post.id}`}>Cancel schedule</button>
                    </>
                  )}
                  <button onClick={() => setPreviewPost(post)} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary transition-colors" data-testid={`preview-post-btn-${post.id}`} title="Preview post">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditPost(post)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors" data-testid={`edit-post-btn-${post.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(post)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-post-btn-${post.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 whitespace-pre-wrap">{post.content}</p>
              {post.images?.length > 0 && <div className="flex gap-2 mt-3 overflow-hidden">{post.images.slice(0, 4).map(image => <img key={image.id} src={image.content_url} alt={image.alt_text || image.filename} className="w-16 h-12 object-cover rounded-md border border-white/10" />)}{post.images.length > 4 && <div className="w-16 h-12 rounded-md bg-secondary/40 border border-white/10 flex items-center justify-center text-xs text-muted-foreground">+{post.images.length - 4}</div>}</div>}
              {post.campaign_playbook_id && (
                <p className="text-[11px] text-muted-foreground mt-3 font-mono" data-testid={`post-campaign-generation-${post.id}`}>
                  Originating campaign: {post.campaign_playbook_name || post.campaign || post.campaign_playbook_id}
                  {post.playbook_next_generation_at
                    ? ` · Next AI generation ${new Date(`${String(post.playbook_next_generation_at).endsWith('Z') ? post.playbook_next_generation_at : `${post.playbook_next_generation_at}Z`}`).toLocaleString(undefined, { timeZone: post.playbook_timezone || undefined })} (${post.playbook_timezone || 'UTC'})`
                    : ' · No recurring generation scheduled'}
                  {' · '}Generation schedule does not publish this post.
                </p>
              )}
              {post.scheduled_at && post.status === 'scheduled' && <p className="text-xs text-amber-300 mt-3 font-mono" data-testid={`post-publish-scheduled-${post.id}`}>LinkedIn publish scheduled for {new Date(`${post.scheduled_at}Z`).toLocaleString()}</p>}
              {post.last_publish_error && <p className="text-xs text-destructive mt-3">{post.last_publish_error}</p>}
            </div>
          ))}
        </div>
      )}

      {showAIModal && <AIGenerateModal onClose={() => setShowAIModal(false)} onCreated={post => setPosts([post, ...posts])} />}
      {editPost && <EditPostModal post={editPost} onClose={() => setEditPost(null)} onSaved={(saved, createdCopy) => { setPosts(current => createdCopy ? [saved, ...current] : current.map(post => post.id === saved.id ? saved : post)); setEditPost(null); }} />}
      {previewPost && <PostPreviewModal post={previewPost} onClose={() => setPreviewPost(null)} />}
      {schedulePost && <SchedulePostModal post={schedulePost} identities={identities} onClose={() => setSchedulePost(null)} onScheduled={saved => setPosts(posts.map(post => post.id === saved.id ? saved : post))} />}
    </div>
  );
}
